import { useEffect, useRef, useCallback, useState } from 'react';
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Enhanced WebSocket hook that receives BOTH session updates AND new messages
 * No more polling needed!
 */
export default function useEnhancedSessionsStream(agentId, onSessionsUpdate, onNewMessages, options = {}) {
  const {
    maxReconnectAttempts = 5,
    initialReconnectDelay = 1000,
    maxReconnectDelay = 30000,
    enableLogging = false
  } = options;

  const [sessions, setSessions] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isUnmountingRef = useRef(false);
  const isConnectingRef = useRef(false); // Prevent multiple simultaneous connections
  const reconnectAttemptsRef = useRef(0);
  const reconnectDelayRef = useRef(initialReconnectDelay);

  // Store callbacks in refs to avoid dependency changes triggering reconnects
  const onSessionsUpdateRef = useRef(onSessionsUpdate);
  const onNewMessagesRef = useRef(onNewMessages);

  // Update refs when callbacks change
  useEffect(() => {
    onSessionsUpdateRef.current = onSessionsUpdate;
    onNewMessagesRef.current = onNewMessages;
  }, [onSessionsUpdate, onNewMessages]);

  const log = useCallback((message, ...args) => {
    if (enableLogging) {
      logger.log(`[WebSocket ${agentId}]`, message, ...args);
    }
  }, [agentId, enableLogging]);

  const connect = useCallback(() => {
    if (!agentId || isUnmountingRef.current) {
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      log('Connection already in progress, skipping');
      return;
    }

    // Don't reconnect if already connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      log('Already connected, skipping');
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      log('Connection in progress, skipping');
      return;
    }

    isConnectingRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        log('Error closing existing WebSocket:', e);
      }
      wsRef.current = null;
    }

    const wsBase = BACKEND_URL.replace(/^http/, 'ws');
    const wsUrl = `${wsBase}/api/sessions/stream/${encodeURIComponent(agentId)}`;

    log('Connecting to:', wsUrl);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        log('✅ Connected');
        isConnectingRef.current = false;
        reconnectDelayRef.current = initialReconnectDelay;
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);

          switch (payload.type) {
            case 'sessions_update':
              log('📨 Sessions updated:', payload.sessions?.length);
              if (payload.sessions) {
                setSessions(payload.sessions);
                if (onSessionsUpdateRef.current) {
                  onSessionsUpdateRef.current(payload.sessions);
                }
              }
              break;

            case 'new_messages':
              log('💬 New messages for session:', payload.session_id);
              if (onNewMessagesRef.current && payload.messages) {
                onNewMessagesRef.current(payload.session_id, payload.messages);
              }
              break;

            case 'ping':
              log('💓 Heartbeat');
              break;

            case 'error':
              log('❌ Server error:', payload.error);
              break;

            default:
              log('Unknown message type:', payload.type);
          }
        } catch (e) {
          log('Error parsing message:', e);
        }
      };

      ws.onclose = (event) => {
        log('Connection closed:', event.code, event.reason);
        wsRef.current = null;
        isConnectingRef.current = false;

        if (isUnmountingRef.current) {
          return;
        }

        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          log(`Max reconnection attempts (${maxReconnectAttempts}) reached`);
          return;
        }

        const delay = Math.min(
          reconnectDelayRef.current * 2,
          maxReconnectDelay
        ) + Math.random() * 1000;

        reconnectDelayRef.current = Math.min(
          reconnectDelayRef.current * 2,
          maxReconnectDelay
        );
        reconnectAttemptsRef.current += 1;

        log(`Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };

      ws.onerror = (err) => {
        log('WebSocket error:', err);
        isConnectingRef.current = false;
        try {
          ws.close();
        } catch (e) {
          log('Error closing WebSocket after error:', e);
        }
      };

    } catch (e) {
      log('Failed to create WebSocket:', e);
      isConnectingRef.current = false;

      if (!isUnmountingRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, 2000);
      }
    }
  }, [agentId, log, maxReconnectAttempts, initialReconnectDelay, maxReconnectDelay]);

  const reconnect = useCallback(() => {
    log('Manual reconnection triggered');
    isConnectingRef.current = false; // Reset connecting flag
    reconnectAttemptsRef.current = 0;
    reconnectDelayRef.current = initialReconnectDelay;
    connect();
  }, [connect, log, initialReconnectDelay]);

  // Only reconnect when agentId changes, not when callbacks change
  useEffect(() => {
    isUnmountingRef.current = false;
    connect();

    return () => {
      log('Cleaning up');
      isUnmountingRef.current = true;
      isConnectingRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          log('Error closing WebSocket during cleanup:', e);
        }
        wsRef.current = null;
      }
    };
  }, [agentId]); // Only depend on agentId, not connect or log

  return {
    sessions,
    reconnect,
    connected: wsRef.current?.readyState === WebSocket.OPEN,
    reconnectAttempts: reconnectAttemptsRef.current
  };
}